import { useRef, useState } from 'react';
import {
  AUTO_STATUS,
  PHONE_TEXT_RE,
  STATUS_PRESETS,
  type NameDisplayMode,
  type PublicUser,
} from '@avslack/shared';
import { useUpdateProfile } from '../data/workspace';
import { uploadSingleImage } from '../data/uploads';
import { Dialog, useAnimatedClose } from './Dialog';
import { useConfirm } from './Confirm';
import { Avatar } from './Avatar';
import { AvatarCropper } from './AvatarCropper';
import { Icon } from './Icon';

/**
 * Sajat profil es statusz.
 *
 * A statusz lejarattal all be: aki beallitja, hogy meetingen van, ne felejtse
 * ott ket napra. A lejart statusz magatol eltunik, nem kell leszedni.
 */
export function ProfileDialog({ me, onClose: closeNow }: { me: PublicUser; onClose: () => void }) {
  const onClose = useAnimatedClose(closeNow);
  const updateProfile = useUpdateProfile();
  const confirm = useConfirm();

  const [displayName, setDisplayName] = useState(me.displayName);
  const [nickname, setNickname] = useState(me.nickname ?? '');
  const [nameDisplay, setNameDisplay] = useState<NameDisplayMode>(me.nameDisplay);
  const [title, setTitle] = useState(me.title ?? '');
  const [department, setDepartment] = useState(me.department ?? '');
  const [phone, setPhone] = useState(me.phone ?? '');
  const [extension, setExtension] = useState(me.extension ?? '');
  const [statusEmoji, setStatusEmoji] = useState(me.statusEmoji ?? '');
  const [statusText, setStatusText] = useState(me.statusText ?? '');
  const [expiresMinutes, setExpiresMinutes] = useState<number | null>(null);

  const fileInput = useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  /**
   * A kivalasztott, MEG NEM feltoltott kep.
   *
   * A vagas a feltoltes ELOTT tortenik: igy a tarolora mar a vegleges,
   * negyzetes kep kerul, es nem marad a szerveren egy nyers kep, amibol a
   * felhasznalo csak egy reszt akart megmutatni.
   */
  const [toCrop, setToCrop] = useState<File | null>(null);

  /** Fokusz mod: a hatralevo percek, vagy `null` ha ki van kapcsolva. */
  const focusLeft =
    me.focusUntil && new Date(me.focusUntil).getTime() > Date.now()
      ? Math.ceil((new Date(me.focusUntil).getTime() - Date.now()) / 60_000)
      : null;

  /**
   * Profilkep.
   *
   * A mentes AZONNAL megtortenik, nem a "Mentes" gombra var: a kep
   * kivalasztasa maga a dontes, es a felhasznalo latni akarja az eredmenyt.
   */
  const pickAvatar = async (file: File) => {
    setToCrop(null);
    setUploading(true);
    setUploadError(null);
    try {
      const fileId = await uploadSingleImage(file);
      await updateProfile.mutateAsync({ avatarFileId: fileId });
    } catch (error) {
      setUploadError(error instanceof Error ? error.message : 'A feltöltés nem sikerült.');
    } finally {
      setUploading(false);
    }
  };

  const toggleFocus = async (minutes: number | null) => {
    await updateProfile.mutateAsync({
      focusUntil: minutes ? new Date(Date.now() + minutes * 60_000).toISOString() : null,
    });
  };

  const applyPreset = (preset: (typeof STATUS_PRESETS)[number]) => {
    setStatusEmoji(preset.emoji);
    setStatusText(preset.text);
    setExpiresMinutes(preset.minutes);
  };

  const clearStatus = () => {
    setStatusEmoji('');
    setStatusText('');
    setExpiresMinutes(null);
  };

  /*
    A nevjegy-mezok ELLENORZESE mar itt: ugyanaz a szabaly, mint a szerveren,
    de igy a hiba a mezo alatt jelenik meg, nem egy altalanos hibakent.
  */
  const phoneProblem =
    phone.trim() && !PHONE_TEXT_RE.test(phone.trim()) ? 'Érvénytelen telefonszám.' : null;
  const extensionProblem =
    extension.trim() && !/^[\d*#]{1,10}$/.test(extension.trim())
      ? 'A mellék csak számjegy lehet.'
      : null;
  const [saveError, setSaveError] = useState<string | null>(null);

  const save = async () => {
    if (phoneProblem || extensionProblem) return;
    setSaveError(null);
    const hasStatus = Boolean(statusText.trim() || statusEmoji.trim());

    try {
      await updateProfile.mutateAsync({
        displayName: displayName.trim() || me.displayName,
        nickname: nickname.trim() || null,
        nameDisplay,
        title: title.trim() || null,
        department: department.trim() || null,
        phone: phone.trim() || null,
        extension: extension.trim() || null,
        statusEmoji: hasStatus ? statusEmoji.trim() || null : null,
        statusText: hasStatus ? statusText.trim() || null : null,
        statusExpiresAt:
          hasStatus && expiresMinutes
            ? new Date(Date.now() + expiresMinutes * 60_000).toISOString()
            : null,
      });
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : 'A mentés nem sikerült.');
      return;
    }
    onClose();
  };

  return (
    <Dialog
      open
      onOpenChange={(next) => !next && onClose()}
      title="Profil és státusz"
      description="Ezt látják rólad a kollégák."
    >
      <div className="mt-5">
        <div className="mb-5 flex items-center gap-3">
          <Avatar
            userId={me.id}
            name={displayName || me.displayName}
            avatarUrl={me.avatarUrl}
            size="lg"
          />
          <div className="min-w-0 flex-1">
            <p className="truncate font-semibold">{displayName || me.displayName}</p>
            <p className="truncate text-sm text-ink-muted">{me.email}</p>

            <div className="mt-1.5 flex flex-wrap items-center gap-2">
              <button
                type="button"
                disabled={uploading}
                onClick={() => fileInput.current?.click()}
                className="rounded-control bg-surface-sunken px-2.5 py-1 text-xs text-ink-muted transition-colors hover:bg-surface-hover hover:text-ink disabled:opacity-50"
              >
                {uploading ? 'Feltöltés…' : me.avatarUrl ? 'Kép cseréje' : 'Kép feltöltése'}
              </button>

              {me.avatarUrl && (
                <button
                  type="button"
                  disabled={uploading}
                  onClick={async () => {
                    const ok = await confirm({
                      title: 'Profilkép törlése',
                      body: 'A kép helyett a monogramod jelenik meg. Később újra feltölthetsz egyet.',
                      confirmLabel: 'Törlés',
                      danger: true,
                    });
                    if (!ok) return;
                    setUploadError(null);
                    try {
                      await updateProfile.mutateAsync({ avatarFileId: null });
                    } catch {
                      setUploadError('A kép törlése nem sikerült.');
                    }
                  }}
                  className="rounded-control px-2 py-1 text-xs text-ink-faint transition-colors hover:text-danger"
                >
                  Törlés
                </button>
              )}
            </div>

            {uploadError && (
              <p role="alert" className="mt-1 text-xs text-danger">
                {uploadError}
              </p>
            )}

            <input
              ref={fileInput}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(event) => {
                const file = event.target.files?.[0];
                event.target.value = '';
                // Eloszor a vago jon; feltoltes csak a jovahagyas utan.
                if (file) {
                  setUploadError(null);
                  setToCrop(file);
                }
              }}
            />
          </div>
        </div>

        {/* ─── Fokusz mod ─────────────────────────────────────────────────── */}
        <div className="mb-5 rounded-card border border-border-soft bg-surface-sunken p-3">
          <p className="text-sm font-medium">
            <span aria-hidden className="mr-1">
              {AUTO_STATUS.focus.emoji}
            </span>
            Fókusz mód
          </p>
          <p className="mt-0.5 text-xs text-ink-muted">
            {focusLeft
              ? `Bekapcsolva, még ${focusLeft} perc.`
              : 'Jelzi a többieknek, hogy most nem szeretnél megszakítást.'}
          </p>

          <div className="mt-2 flex flex-wrap gap-1.5">
            {focusLeft ? (
              <button
                type="button"
                onClick={() => void toggleFocus(null)}
                className="rounded-control bg-surface-raised px-2.5 py-1 text-xs font-medium"
              >
                Kikapcsolás
              </button>
            ) : (
              [30, 60, 120].map((minutes) => (
                <button
                  key={minutes}
                  type="button"
                  onClick={() => void toggleFocus(minutes)}
                  className="rounded-control bg-surface-raised px-2.5 py-1 text-xs transition-colors hover:bg-surface-hover"
                >
                  {minutes < 60 ? `${minutes} perc` : `${minutes / 60} óra`}
                </button>
              ))
            )}
          </div>
        </div>

        <Field label="Megjelenített név" htmlFor="profile-name">
          <input
            id="profile-name"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            className="w-full rounded-control border border-border-soft bg-surface px-3 py-2 text-base outline-none focus:border-accent"
          />
        </Field>

        {/*
          A felhasznalonev NEM szerkesztheto itt.
          Belepesi azonosito: ha barki barmikor atirhatna, egy elgepeles utan
          nem tudna belepni - a csere admin dolga.
        */}
        {me.username && (
          <Field label="Felhasználónév" htmlFor="profile-username">
            <input
              id="profile-username"
              value={me.username}
              readOnly
              className="w-full cursor-default rounded-control border border-border-soft bg-surface-sunken px-3 py-2 text-base text-ink-muted outline-none"
            />
            <p className="mt-1 text-2xs text-ink-faint">
              Ezzel is be tudsz lépni, nem csak az e-mail címeddel. Cseréjéhez szólj egy adminnak.
            </p>
          </Field>
        )}

        <Field label="Becenév" htmlFor="profile-nickname" optional>
          <input
            id="profile-nickname"
            value={nickname}
            onChange={(e) => setNickname(e.target.value)}
            placeholder="pl. Ádi"
            className="w-full rounded-control border border-border-soft bg-surface px-3 py-2 text-base outline-none placeholder:text-ink-faint focus:border-accent"
          />
          <p className="mt-1 text-2xs text-ink-faint">
            Csak annak jelenik meg, aki a beceneveket kérte — lentebb te is átállíthatod, mit
            szeretnél látni.
          </p>
        </Field>

        <Field label="Beosztás" htmlFor="profile-title" optional>
          <input
            id="profile-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="pl. Állatorvos"
            className="w-full rounded-control border border-border-soft bg-surface px-3 py-2 text-base outline-none placeholder:text-ink-faint focus:border-accent"
          />
        </Field>

        {/* A Munkatarsak nevjegyzekeben latszik (AD2). */}
        <Field label="Osztály" htmlFor="profile-department" optional>
          <input
            id="profile-department"
            value={department}
            onChange={(e) => setDepartment(e.target.value)}
            placeholder="pl. Ügyfélszolgálat"
            className="w-full rounded-control border border-border-soft bg-surface px-3 py-2 text-base outline-none placeholder:text-ink-faint focus:border-accent"
          />
        </Field>

        <div className="grid grid-cols-[1fr_7rem] gap-3">
          <Field label="Telefonszám" htmlFor="profile-phone" optional>
            <input
              id="profile-phone"
              type="tel"
              inputMode="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+36 30 123 4567"
              aria-invalid={Boolean(phoneProblem)}
              className="w-full rounded-control border border-border-soft bg-surface px-3 py-2 text-base outline-none placeholder:text-ink-faint focus:border-accent"
            />
            {phoneProblem && <p className="mt-1 text-2xs text-danger">{phoneProblem}</p>}
          </Field>
          <Field label="Mellék" htmlFor="profile-extension" optional>
            <input
              id="profile-extension"
              inputMode="numeric"
              value={extension}
              onChange={(e) => setExtension(e.target.value)}
              placeholder="214"
              aria-invalid={Boolean(extensionProblem)}
              className="w-full rounded-control border border-border-soft bg-surface px-3 py-2 text-base outline-none placeholder:text-ink-faint focus:border-accent"
            />
            {extensionProblem && <p className="mt-1 text-2xs text-danger">{extensionProblem}</p>}
          </Field>
        </div>

        {/*
          A NEZO dontese: kit hogyan latok.
          Nem a nev tulajdonosa donti el, hogy ot becenevvel hivjak-e -
          ez a sajat nezetem beallitasa, es csak nekem szol.
        */}
        <div className="mt-5 border-t border-border-soft pt-4">
          <p className="mb-1 text-sm font-semibold">Hogyan látod a kollégákat?</p>
          <p className="mb-2 text-xs text-ink-muted">
            Csak a te felületedre vonatkozik. Akinek nincs beceneve, annál a rendes neve jelenik
            meg.
          </p>

          <div className="flex gap-1.5">
            {(
              [
                ['nev', 'Rendes nevek'],
                ['becenev', 'Becenevek'],
              ] as const
            ).map(([value, label]) => (
              <button
                key={value}
                type="button"
                aria-pressed={nameDisplay === value}
                onClick={() => setNameDisplay(value)}
                className={`rounded-control px-3 py-1.5 text-sm transition-colors ${
                  nameDisplay === value
                    ? 'bg-accent font-semibold text-accent-ink'
                    : 'bg-surface-sunken text-ink-muted hover:text-ink'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-5 border-t border-border-soft pt-4">
          <p className="mb-2 text-sm font-semibold">Státusz</p>

          <div className="mb-3 flex flex-wrap gap-1.5">
            {STATUS_PRESETS.map((preset) => (
              <button
                key={preset.text}
                type="button"
                aria-pressed={statusText === preset.text}
                onClick={() => applyPreset(preset)}
                className={[
                  'flex items-center gap-1.5 rounded-pill border px-2.5 py-1 text-xs transition-colors duration-(--duration-quick)',
                  statusText === preset.text
                    ? 'border-accent bg-accent-soft text-accent'
                    : 'border-border-soft hover:border-border-strong',
                ].join(' ')}
              >
                <span aria-hidden="true">{preset.emoji}</span>
                {preset.text}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2 rounded-control border border-border-soft bg-surface px-3 py-2 focus-within:border-accent">
            <input
              value={statusEmoji}
              onChange={(e) => setStatusEmoji(e.target.value)}
              placeholder="🙂"
              aria-label="Státusz emoji"
              className="w-8 bg-transparent text-center text-base outline-none"
            />
            <span aria-hidden="true" className="h-5 w-px bg-border-soft" />
            <input
              value={statusText}
              onChange={(e) => setStatusText(e.target.value)}
              placeholder="Mivel foglalkozol éppen?"
              aria-label="Státusz szöveg"
              className="w-full bg-transparent text-base outline-none placeholder:text-ink-faint"
            />
            {(statusText || statusEmoji) && (
              <button
                type="button"
                onClick={clearStatus}
                title="Státusz törlése"
                aria-label="Státusz törlése"
                className="shrink-0 rounded px-1 text-ink-faint hover:text-ink"
              >
                <Icon name="bezaras" />
              </button>
            )}
          </div>

          <div className="mt-2 flex flex-wrap items-center gap-1.5 text-xs">
            <span className="text-ink-muted">Érvényes:</span>
            {[
              { label: '30 perc', minutes: 30 },
              { label: '1 óra', minutes: 60 },
              { label: '4 óra', minutes: 240 },
              { label: 'Ma végéig', minutes: minutesUntilEndOfDay() },
              { label: 'Nem jár le', minutes: null },
            ].map((option) => (
              <button
                key={option.label}
                type="button"
                aria-pressed={expiresMinutes === option.minutes}
                onClick={() => setExpiresMinutes(option.minutes)}
                className={[
                  'rounded-pill border px-2 py-0.5 transition-colors duration-(--duration-quick)',
                  expiresMinutes === option.minutes
                    ? 'border-accent bg-accent-soft text-accent'
                    : 'border-border-soft text-ink-muted hover:border-border-strong',
                ].join(' ')}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {saveError && (
          <p role="alert" className="mt-4 text-sm text-danger">
            {saveError}
          </p>
        )}
        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-control px-4 py-2 text-sm font-medium text-ink-muted transition-colors duration-(--duration-quick) hover:bg-surface-hover hover:text-ink"
          >
            Mégse
          </button>
          <button
            type="button"
            onClick={() => void save()}
            disabled={updateProfile.isPending || Boolean(phoneProblem || extensionProblem)}
            className="rounded-control bg-accent px-4 py-2 text-sm font-semibold text-accent-ink transition-colors duration-(--duration-quick) hover:bg-accent-hover disabled:opacity-40"
          >
            {updateProfile.isPending ? 'Mentés…' : 'Mentés'}
          </button>
        </div>
      </div>

      {toCrop && (
        <AvatarCropper
          file={toCrop}
          onCancel={() => setToCrop(null)}
          onDone={(cropped) => void pickAvatar(cropped)}
        />
      )}
    </Dialog>
  );
}

function minutesUntilEndOfDay(): number {
  const end = new Date();
  end.setHours(23, 59, 0, 0);
  return Math.max(1, Math.round((end.getTime() - Date.now()) / 60_000));
}

function Field({
  label,
  htmlFor,
  optional,
  children,
}: {
  label: string;
  htmlFor: string;
  optional?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="mt-4 first:mt-0">
      <label htmlFor={htmlFor} className="mb-1.5 block text-sm font-semibold">
        {label}
        {optional && <span className="ml-1.5 font-normal text-ink-faint">nem kötelező</span>}
      </label>
      {children}
    </div>
  );
}
